// The rules the Insights tab is built on, kept out of the component so they
// can be tested without a browser.
//
// The one that matters: when coverage is short every total is a FLOOR. The
// screen must say "at least 467 hours" and why, never "467 hours" — a number
// computed over two thirds of a roster, printed as though it were the month,
// is how a dashboard becomes a liability.
import type { AttendanceTrust } from '@/services/attendanceService';

export type Tone = 'success' | 'warning' | 'danger' | 'muted';

export const VERDICT_TONE: Record<AttendanceTrust['verdict'], Tone> = {
  good: 'success',
  partial: 'warning',
  poor: 'danger',
  no_data: 'muted',
};

/** Presets, not a date picker: the questions this tab answers are monthly. */
export type PeriodKey = 'this_month' | 'last_month' | 'last_3_months';

const iso = (d: Date) => d.toISOString().slice(0, 10);

export const periodRange = (key: PeriodKey, today = new Date()): { from: string; to: string } => {
  const y = today.getFullYear();
  const m = today.getMonth();
  if (key === 'last_month') {
    return { from: iso(new Date(Date.UTC(y, m - 1, 1))), to: iso(new Date(Date.UTC(y, m, 0))) };
  }
  if (key === 'last_3_months') {
    return { from: iso(new Date(Date.UTC(y, m - 2, 1))), to: iso(new Date(Date.UTC(y, m + 1, 0))) };
  }
  return { from: iso(new Date(Date.UTC(y, m, 1))), to: iso(new Date(Date.UTC(y, m + 1, 0))) };
};

/** Whole days between `covered_through` and today. Null when nothing is held. */
export const daysBehind = (
  coveredThrough: string | null,
  periodEnd?: string | null,
  today = new Date(),
): number | null => {
  if (!coveredThrough) return null;
  const then = Date.parse(`${coveredThrough}T00:00:00Z`);
  if (Number.isNaN(then)) return null;
  const now = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  // ⚠️ Behind WHAT. A period that has already ended cannot be behind: looking
  // at September on 5 October, coverage through 30 September is COMPLETE, and
  // calling it "5d behind" sent somebody hunting for data that was never
  // going to exist. The yardstick is the earlier of today and the period end.
  const end = periodEnd ? Date.parse(`${periodEnd}T00:00:00Z`) : NaN;
  const upTo = Number.isNaN(end) ? now : Math.min(now, end);
  return Math.max(0, Math.round((upTo - then) / 86400000));
};

/** Minutes as hours, to ONE place — the single rounding for this screen.
 *  The server also computes `approved_hours` to two places; using both put
 *  2.18 and 2.2 for the same 131 minutes on one card. The UI rounds from
 *  minutes so every figure agrees. */
export const hoursOf = (minutes: number): number => Math.round((minutes || 0) / 60 * 10) / 10;

/** A month bucket's label. Pinned to UTC: formatting a UTC midnight in the
 *  viewer's own zone put September's bar under "Aug 26" west of Greenwich. */
export const monthLabel = (m: string): string => {
  const [y, mm] = m.split('-').map(Number);
  if (!y || !mm) return m;
  return new Date(Date.UTC(y, mm - 1, 1))
    .toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' });
};

/** The sentence under the coverage figure. It states what is missing in the
 *  same breath as what is held — never one without the other. */
export const coverageSentence = (t: AttendanceTrust): string => {
  if (!t.roster_available) {
    return 'The employee list could not be read, so coverage cannot be worked out — the totals below are only what is on file.';
  }
  if (t.expected_days === 0) {
    return 'No working days in this period, so there is nothing to cover.';
  }
  const missing = t.expected_days - t.covered_days;
  // ⚠️ `covered_days` is "has a scan OR approved leave"; `recorded_days` is
  // the scans. Labelling covered as "have a record" overstated the records by
  // the number of leave days, and the SAME screen prints recorded_days as
  // "the N working days we hold" — so it contradicted itself (117 vs 115) and
  // nobody could make the figures add up.
  //
  // leaveOnly, not leave_days: somebody who scanned WHILE on approved leave is
  // counted in both, and adding the two would exceed the covered total. A day
  // explained by leave is one that needed explaining — no scan. The scan-on-
  // leave case is an exception elsewhere, not coverage here.
  const leaveOnly = Math.max(0, t.covered_days - t.recorded_days);
  if (missing === 0) {
    return leaveOnly > 0
      ? `All ${t.expected_days} expected person-days are accounted for — `
        + `${t.recorded_days} with a record and ${leaveOnly} by approved leave.`
      : `All ${t.expected_days} expected person-days have a record.`;
  }
  const breakdown = leaveOnly > 0
    ? `${t.covered_days} of ${t.expected_days} expected person-days are accounted for — `
      + `${t.recorded_days} with a record and ${leaveOnly} by approved leave`
    : `${t.recorded_days} of ${t.expected_days} expected person-days have a record`;
  return `${breakdown}. ${missing} do not.`;
};

/** The qualifier the cost panel wears when the data is incomplete. */
export const costQualifier = (t: AttendanceTrust): string | null =>
  t.totals_are_complete ? null
    : `At least — ${Math.round(100 - t.coverage_pct)}% of expected days have no record, so these are floors, not the month's total.`;

/** Every month the selected range touches, in order.
 *
 * The chart is built from the RANGE, not from the months that happen to have
 * data. A three-month view where only September had overtime otherwise drew
 * a single bar — and a lone bar is not a comparison, it is the number above
 * it drawn larger. Empty months belong on the axis as zeros: "none in August"
 * is a finding. (Same convention as LeaveByMonthChart's full-year domain.) */
export const monthsInRange = (from: string, to: string): string[] => {
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  if (!fy || !fm || !ty || !tm) return [];
  const out: string[] = [];
  for (let y = fy, m = fm; y < ty || (y === ty && m <= tm); m === 12 ? (m = 1, y++) : m++) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    if (out.length > 24) break; // the endpoint caps the range; belt and braces
  }
  return out;
};

/** The names to print for "never seen", and what is left unsaid.
 *
 *  ⚠️ The banner printed a count of 36 and then listed 8 names, reading as
 *  though those eight WERE the thirty-six. A list that silently truncates is
 *  worse than a shorter one: somebody fixes the names they can see and
 *  believes they are done.
 */
export const neverSeenNames = (names: string[], limit = 8): string => {
  const shown = names.slice(0, limit);
  const rest = names.length - shown.length;
  if (rest <= 0) return shown.join(', ');
  return `${shown.join(', ')} and ${rest} other${rest === 1 ? '' : 's'}`;
};
